import { OPS_LOGS_FIRST_PAGE, OPS_LOGS_WINDOW } from '@/function/ops/ops.live.constants';
import opsLogsMerge from '@/function/ops/ops.logs.merge';
import { dashboardApi } from '@/store/api/base/api.base';
import type { TypeAppDispatch, TypeRootState } from '@/store/store';
import type { TypePageInfo } from '@/types/api/types.api';
import type {
    TypeOpsAccess,
    TypeOpsAction,
    TypeOpsActionRun,
    TypeOpsRun,
    TypeOpsRunDetail,
    TypeOpsRunReply,
} from '@/types/ops/types.ops.action';
import type { TypeOpsActivity, TypeOpsActivityQuery } from '@/types/ops/types.ops.activity';
import type { TypeOpsAlert } from '@/types/ops/types.ops.alert';
import type { TypeOpsDeploy, TypeOpsDeployOverview } from '@/types/ops/types.ops.deploy';
import type { TypeOpsLogEntry, TypeOpsTarget } from '@/types/ops/types.ops.log';
import type {
    TypeOpsPaged,
    TypeOpsRunnerKey,
    TypeOpsServer,
    TypeOpsServerCredential,
    TypeOpsServerInput,
    TypeOpsServerPatch,
    TypeOpsServerRegistry,
    TypeOpsServersWire,
} from '@/types/ops/types.ops.server';

type TypeOpsServers = {
    servers: TypeOpsServer[];
    serverTime: number;
    clockOffset: number;
};

type TypeOpsLogs = {
    server: string;
    container: string;
    entries: TypeOpsLogEntry[];
    gaps: number;
};

type TypeOpsRunInput = { server: string; command: string; params: Record<string, string>; wait?: boolean };

export type TypeOpsServerQuery = { server: string };

export type TypeOpsAlertsQuery = { server: string; state: 'firing' | 'resolved' | '' };

type TypeLogsWire = { server: string; container: string; entries: TypeOpsLogEntry[] };

const ACTIONS_PAGE = 30;

const RUNS_PAGE = 30;

const ALERTS_PAGE = 50;

const DEPLOYS_PAGE = 10;

const ACTIVITY_PAGE = 50;

export const withClock = (data: TypeOpsServersWire): TypeOpsServers => ({
    servers: Array.isArray(data?.servers) ? data.servers : [],
    serverTime: Number(data?.serverTime) || Date.now(),
    clockOffset: (Number(data?.serverTime) || Date.now()) - Date.now(),
});

const filled = (params: Record<string, string | number | null | undefined>): Record<string, string | number> =>
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)) as Record<string, string | number>;

const paged = <T>(limit: number) => (data: TypeOpsPaged<T> | null): TypeOpsPaged<T> => {
    const items = Array.isArray(data?.items) ? data!.items : [];
    const page: Partial<TypePageInfo> = data?.page ?? {};
    return {
        items,
        page: {
            limit: Number(page.limit) || limit,
            count: Number(page.count) || items.length,
            hasMore: page.hasMore === true && typeof page.nextCursor === 'string' && page.nextCursor !== '',
            nextCursor: typeof page.nextCursor === 'string' && page.nextCursor ? page.nextCursor : null,
        },
    };
};

const CURSOR = {
    initialPageParam: null as string | null,
    getNextPageParam: (last: TypeOpsPaged<unknown>) => (last.page.hasMore && last.page.nextCursor ? last.page.nextCursor : undefined),
};

export const opsApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        readOpsServers: build.query<TypeOpsServers, void>({
            query: () => ({ url: '/ops/servers' }),
            transformResponse: withClock,
            keepUnusedDataFor: 30,
        }),
        readOpsAccess: build.query<TypeOpsAccess, void>({
            query: () => ({ url: '/ops/access' }),
            keepUnusedDataFor: 30,
        }),
        readOpsLogs: build.query<TypeOpsLogs, TypeOpsTarget>({
            query: ({ server, container }) => ({ url: '/ops/logs', params: { server, container, limit: OPS_LOGS_FIRST_PAGE } }),
            transformResponse: (data: TypeLogsWire, _meta, arg): TypeOpsLogs => ({
                server: arg.server,
                container: arg.container,
                entries: opsLogsMerge([], Array.isArray(data?.entries) ? data.entries : [], OPS_LOGS_WINDOW),
                gaps: 0,
            }),
            merge: (cache, incoming) => {
                cache.entries = opsLogsMerge(cache.entries, incoming.entries, OPS_LOGS_WINDOW);
            },
            keepUnusedDataFor: 0,
        }),
        createOpsServer: build.mutation<TypeOpsServerCredential, TypeOpsServerInput>({
            query: (data) => ({ url: '/ops/servers', method: 'post', data }),
        }),
        updateOpsServer: build.mutation<TypeOpsServerRegistry, { name: string; patch: TypeOpsServerPatch }>({
            query: ({ name, patch }) => ({ url: `/ops/servers/${encodeURIComponent(name)}`, method: 'patch', data: patch }),
        }),
        rotateOpsServer: build.mutation<TypeOpsServerCredential, string>({
            query: (name) => ({ url: `/ops/servers/${encodeURIComponent(name)}/rotate`, method: 'post', data: {} }),
        }),
        pinOpsRunnerKey: build.mutation<{ server: string; runnerKey: TypeOpsRunnerKey }, { name: string; publicKey: string; replaces: string }>({
            query: ({ name, publicKey, replaces }) => ({ url: `/ops/servers/${encodeURIComponent(name)}/runner-key`, method: 'put', data: { publicKey, replaces } }),
        }),
        unpinOpsRunnerKey: build.mutation<{ server: string; runnerKey: null }, { name: string; replaces: string }>({
            query: ({ name, replaces }) => ({ url: `/ops/servers/${encodeURIComponent(name)}/runner-key`, method: 'delete', params: { replaces } }),
        }),
        removeOpsServer: build.mutation<{ name: string }, string>({
            query: (name) => ({ url: `/ops/servers/${encodeURIComponent(name)}`, method: 'delete' }),
        }),
        pageOpsActions: build.infiniteQuery<TypeOpsPaged<TypeOpsActionRun>, TypeOpsServerQuery, string | null>({
            infiniteQueryOptions: CURSOR,
            query: ({ queryArg, pageParam }) => ({ url: '/ops/actions', params: filled({ server: queryArg.server, limit: ACTIONS_PAGE, cursor: pageParam }) }),
            transformResponse: paged<TypeOpsActionRun>(ACTIONS_PAGE),
            keepUnusedDataFor: 30,
        }),
        requestOpsAction: build.mutation<TypeOpsActionRun, { server: string; container: string; action: TypeOpsAction }>({
            query: (data) => ({ url: '/ops/actions', method: 'post', data }),
        }),
        pageOpsRuns: build.infiniteQuery<TypeOpsPaged<TypeOpsRun>, TypeOpsServerQuery, string | null>({
            infiniteQueryOptions: CURSOR,
            query: ({ queryArg, pageParam }) => ({ url: '/ops/commands', params: filled({ server: queryArg.server, limit: RUNS_PAGE, cursor: pageParam }) }),
            transformResponse: paged<TypeOpsRun>(RUNS_PAGE),
            keepUnusedDataFor: 30,
        }),
        readOpsRun: build.query<TypeOpsRunDetail, string>({
            query: (id) => ({ url: `/ops/commands/${encodeURIComponent(id)}` }),
            keepUnusedDataFor: 60,
        }),
        requestOpsRun: build.mutation<TypeOpsRunReply, TypeOpsRunInput>({
            query: ({ server, command, params, wait = false }) => ({ url: '/ops/commands', method: 'post', data: { server, command, params, wait } }),
        }),
        requestOpsHostRun: build.mutation<TypeOpsRunReply, TypeOpsRunInput>({
            query: ({ server, command, params, wait = false }) => ({ url: '/ops/host', method: 'post', data: { server, command, params, wait } }),
        }),
        pageOpsAlerts: build.infiniteQuery<TypeOpsPaged<TypeOpsAlert>, TypeOpsAlertsQuery, string | null>({
            infiniteQueryOptions: CURSOR,
            query: ({ queryArg, pageParam }) => ({ url: '/ops/alerts', params: filled({ server: queryArg.server, state: queryArg.state, limit: ALERTS_PAGE, cursor: pageParam }) }),
            transformResponse: paged<TypeOpsAlert>(ALERTS_PAGE),
            keepUnusedDataFor: 60,
        }),
        readOpsDeployStatus: build.query<TypeOpsDeployOverview, string>({
            query: (server) => ({ url: '/ops/deploys/status', params: { server } }),
            keepUnusedDataFor: 60,
        }),
        pageOpsDeploys: build.infiniteQuery<TypeOpsPaged<TypeOpsDeploy>, TypeOpsServerQuery, string | null>({
            infiniteQueryOptions: CURSOR,
            query: ({ queryArg, pageParam }) => ({ url: '/ops/deploys', params: filled({ server: queryArg.server, limit: DEPLOYS_PAGE, cursor: pageParam }) }),
            transformResponse: paged<TypeOpsDeploy>(DEPLOYS_PAGE),
            keepUnusedDataFor: 60,
        }),
        requestOpsDeploy: build.mutation<TypeOpsDeploy, { server: string; target: string; requestKey: string }>({
            query: ({ target, requestKey }) => ({ url: '/ops/deploys', method: 'post', data: { target, requestKey } }),
        }),
        pageOpsActivity: build.infiniteQuery<TypeOpsPaged<TypeOpsActivity>, TypeOpsActivityQuery, string | null>({
            infiniteQueryOptions: CURSOR,
            query: ({ queryArg, pageParam }) => ({ url: '/ops/activity', params: filled({ ...queryArg, limit: ACTIVITY_PAGE, cursor: pageParam }) }),
            transformResponse: paged<TypeOpsActivity>(ACTIVITY_PAGE),
            keepUnusedDataFor: 30,
        }),
    }),
});

type TypeThunk = (inner: TypeAppDispatch, getState: () => TypeRootState) => void;

export const run = (dispatch: TypeAppDispatch, thunk: TypeThunk): void => { dispatch(thunk as never); };

export const {
    useReadOpsServersQuery, useReadOpsAccessQuery, useReadOpsLogsQuery, useRequestOpsActionMutation,
    useCreateOpsServerMutation, useUpdateOpsServerMutation, useRotateOpsServerMutation, useRemoveOpsServerMutation,
    usePinOpsRunnerKeyMutation, useUnpinOpsRunnerKeyMutation,
    usePageOpsActionsInfiniteQuery, usePageOpsRunsInfiniteQuery, usePageOpsAlertsInfiniteQuery, usePageOpsDeploysInfiniteQuery, usePageOpsActivityInfiniteQuery,
    useReadOpsRunQuery, useRequestOpsRunMutation, useRequestOpsHostRunMutation,
    useReadOpsDeployStatusQuery, useRequestOpsDeployMutation,
} = opsApi;
